import { ValidationErrorEnum } from 'components/Core/WalletForms/validations/src/ValidationErrorEnum';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { featureFlagsSelectors } from 'store/featureFlags/selectors';
import { getStore } from 'store/store';

/**
 * Centralized Splunk logging for frontend validation errors
 * Tracks when users encounter validation errors on input fields
 * This enables dashboard creation for monitoring validation error occurrences by domain ID
 *
 * Logging is controlled by the feature flag 'cp-malformed-error-splunk'
 * E2E ID: 547554, Prod ID: 304995
 */

const splunkReporter = SplunkReporter.getInstance();

/**
 * Check if validation error logging to Splunk is enabled via feature flag
 * Feature flag: cp-malformed-error-splunk (E2E: 547554, Prod: 304995)
 */
const isValidationLoggingEnabled = (): boolean => {
  try {
    const store = getStore();
    if (!store) {
      return false;
    }
    return featureFlagsSelectors.isFeatureFlagEnabled('cp-malformed-error-splunk')(
      store.getState()
    );
  } catch (e) {
    // If we can't access the store or feature flag, default to disabled
    return false;
  }
};

/**
 * Form types for validation error logging
 */
export type FormType =
  | 'creditCard'
  | 'debitCard'
  | 'bankAccount'
  | 'billingAddress'
  | 'paypal'
  | 'applePay'
  | 'googlePay'
  | 'other';

/**
 * Validation error context for logging
 */
export interface ValidationErrorContext {
  /** Name of the field that has the validation error (e.g., 'number', 'expDate', 'cvc', 'name', 'zipCode') */
  fieldName: string;
  /** The validation error enum value */
  errorType: ValidationErrorEnum;
  /** Type of form where the error occurred */
  formType?: FormType;
  /** Logger path for identifying the source component */
  logger?: string;
}

/**
 * Type guard to check if a value is a valid ValidationErrorEnum
 * @param value - The value to check
 * @returns True if the value is a valid ValidationErrorEnum member
 */
export const isValidationErrorEnum = (value: unknown): value is ValidationErrorEnum => {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    typeof ValidationErrorEnum[value] === 'string'
  );
};

/**
 * Get string representation of ValidationErrorEnum with type guards
 * Useful for human-readable logs in Splunk dashboards
 * Accepts unknown input for maximum safety
 *
 * @param errorEnum - The value to convert to an error name
 * @returns The string name of the enum, or UNKNOWN_ERROR_* if invalid
 */
export const getValidationErrorName = (errorEnum: unknown): string => {
  // Type guard: ensure errorEnum is a valid number
  if (typeof errorEnum !== 'number' || !Number.isFinite(errorEnum)) {
    return 'UNKNOWN_ERROR_INVALID_TYPE';
  }

  // Check if the enum value exists in the reverse mapping
  const enumName = ValidationErrorEnum[errorEnum];

  if (typeof enumName === 'string') {
    return enumName;
  }

  return `UNKNOWN_ERROR_${errorEnum}`;
};

/**
 * Log a frontend validation error to Splunk
 * Call this when a validation error is displayed to the user
 *
 * @param context - The validation error context
 *
 * @example
 * ```typescript
 * import { logValidationError, ValidationErrorEnum } from 'businessLogic/utils/validationLogs';
 *
 * // When a card number validation error is shown
 * logValidationError({
 *   fieldName: 'number',
 *   errorType: ValidationErrorEnum.INVALID_CARD_NUMBER,
 *   formType: 'creditCard',
 *   logger: 'components/Core/Payment/PaymentForm/Card/CardForm',
 * });
 * ```
 */
export const logValidationError = ({
  fieldName,
  errorType,
  formType = 'other',
  logger = 'components/Shared/Inputs/ValidatedLabeledInput',
}: ValidationErrorContext): void => {
  // Check feature flag before logging
  if (!isValidationLoggingEnabled()) {
    return;
  }

  try {
    splunkReporter.contextual({
      event: 'pay',
      action: 'formValidation',
      logInfo: { logLevel: 'warn', logger },
      activityInfo: {
        fieldName,
        errorType: getValidationErrorName(errorType),
        errorCode: errorType,
        formType,
        validationResult: 'failed',
      },
    });
  } catch (e) {
    // Silently fail - validation logging should not break the user experience
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Failed to log validation error to Splunk:', (e as Error)?.message);
    }
  }
};

/**
 * Log multiple validation errors at once (e.g., on form submit)
 * Useful when a form has multiple validation errors that should be logged together
 *
 * @param errors - Object mapping field names to validation error enums
 * @param formType - Type of form where errors occurred
 * @param logger - Logger path for identifying the source component
 *
 * @example
 * ```typescript
 * import { logMultipleValidationErrors, ValidationErrorEnum } from 'businessLogic/utils/validationLogs';
 *
 * // When form submit reveals multiple errors
 * logMultipleValidationErrors(
 *   {
 *     number: [ValidationErrorEnum.INVALID_CARD_NUMBER],
 *     expDate: [ValidationErrorEnum.INVALID_EXP_DATE],
 *   },
 *   'creditCard',
 *   'components/Core/Payment/PaymentForm/Card/CardForm'
 * );
 * ```
 */
export const logMultipleValidationErrors = (
  errors: Record<string, ValidationErrorEnum[] | undefined>,
  formType: FormType = 'other',
  logger = 'components/Shared/Inputs/ValidatedLabeledInput'
): void => {
  const errorEntries = Object.entries(errors).filter(([_, value]) => value && value.length > 0);

  if (errorEntries.length === 0) {
    return;
  }

  // Check feature flag before logging
  if (!isValidationLoggingEnabled()) {
    return;
  }

  // Log a summary event with all errors
  try {
    const errorSummary = errorEntries.reduce((acc, [fieldName, errorArray]) => {
      if (errorArray && errorArray.length > 0) {
        const firstError = errorArray[0];
        acc[fieldName] = getValidationErrorName(firstError);
      }
      return acc;
    }, {} as Record<string, string>);

    splunkReporter.contextual({
      event: 'pay',
      action: 'formValidation',
      logInfo: { logLevel: 'warn', logger },
      activityInfo: {
        formType,
        validationResult: 'failed',
        errorCount: errorEntries.length,
        errors: errorSummary,
        fieldNames: errorEntries.map(([fieldName]) => fieldName).join(','),
      },
    });
  } catch (e) {
    // Silently fail
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Failed to log multiple validation errors to Splunk:', (e as Error)?.message);
    }
  }
};
